import { useFxWorkspaceContext } from '@mobile-mapping/providers';
import { t } from '@trimble/i18n';
import { BtnClasses, Button, IconButton } from '@tcweb/connect-ui';
import { Component, For, JSX, Show, createMemo, createSignal } from 'solid-js';

import { CreateDraftLayerDialog } from './CreateDraftLayerDialog';
import { FxDraftLayerRow } from './FxDraftLayerRow';
import { FxWorkspaceEmptyState } from './FxWorkspaceEmptyState';

export interface DraftLayersPanelProps {
  readonly workspaceId: string;
}

export const DraftLayersPanel: Component<DraftLayersPanelProps> = (
  props
): JSX.Element => {
  const fxWorkspace = useFxWorkspaceContext();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = createSignal(false);

  const draftLayers = createMemo(() =>
    fxWorkspace.listDraftLayers(props.workspaceId)
  );

  const openCreateDialog = (): void => {
    setIsCreateDialogOpen(true);
  };

  return (
    <div class="flex h-full flex-col" data-testid="workspace-draft-layers">
      <div class="flex items-center justify-between border-b border-base-200 px-2 py-1">
        <span class="text-sm font-medium">
          {t('mobileMapping.fxWorkspace.draftLayers.title')}
        </span>
        <IconButton
          color={BtnClasses.TERTIARY}
          icon="tc-icon-content-add"
          size="icon-medium"
          data-testid="draft-layers-create-button"
          title={t('mobileMapping.fxWorkspace.draftLayers.createCustomLayer')}
          clicked={openCreateDialog}
        />
      </div>
      <div class="min-h-0 flex-1 overflow-auto">
        <Show
          when={draftLayers().length > 0}
          fallback={
            <FxWorkspaceEmptyState
              icon="tc-icon-layers"
              title={t('mobileMapping.fxWorkspace.draftLayers.title')}
              message={t('mobileMapping.fxWorkspace.draftLayers.emptyMessage')}
              dataTestId="draft-layers-empty"
              content={
                <Button
                  color={BtnClasses.PRIMARY}
                  icon="tc-icon-content-add"
                  data-testid="draft-layers-empty-create-button"
                  onClick={openCreateDialog}
                >
                  {t('general.add')}
                </Button>
              }
            />
          }
        >
          <For each={draftLayers()}>
            {(layer) => (
              <FxDraftLayerRow workspaceId={props.workspaceId} layer={layer} />
            )}
          </For>
        </Show>
      </div>
      <CreateDraftLayerDialog
        show={isCreateDialogOpen()}
        workspaceId={props.workspaceId}
        onClose={() => setIsCreateDialogOpen(false)}
      />
    </div>
  );
};
